import { useLightMe, useTripsAndDepartures } from "@myaccount/_client/hooks";
import { I18nHive, NoIterpString, useI18n } from "@wojo/localization";

import {
    MyAccountBasicMyTripFragment,
    MyAccountTripWithChecklistFragment,
    TripCustomerType,
    TripStatus,
} from "generated/graphql";
import { FiCompass, FiHeart, FiUser } from "react-icons/fi";
import { LuBook, LuHistory } from "react-icons/lu";
import { NavItemProps } from "./NavItem";
import {
    MyAccountBasicTripAndDeparture,
    totalChecklistItemCount,
} from "@myaccount/_client/hooks";
import { gtmSendEvent } from "@wojo/analytics";

type BookingL10n = I18nHive<"account">["account"]["navigation"]["booking"];

// The order here is important, as it is used to determine the order in both the navigation and the overview
export const getChecklistPeerNames = (
    l10n: BookingL10n,
    isGroupCoordinator: boolean,
    isEvalReleased: boolean,
) => {
    return [
        isEvalReleased ? l10n.tourEval : null,
        isGroupCoordinator ? l10n.myGroupMembers : null,
        l10n.travelerDetails,
        l10n.payments,
        l10n.itinerary,
        l10n.customizationOptions,
        l10n.travelProtection,
        l10n.accommodations,
        l10n.flights,
        l10n.gettingReady,
        l10n.entryRequirements,
        l10n.bookingPolicies,
    ].filter(
        (item): item is { text: NoIterpString; slug: NoIterpString } =>
            !!item?.slug.__value && !!item.text.__value,
    );
};

const countActionsPerBooking = (trips?: MyAccountTripWithChecklistFragment[]) =>
    (trips ?? []).reduce((acc, t) => totalChecklistItemCount(t) + acc, 0);

const sendAnalyticsEvent = ({
    section,
    navigationSelection,
}: {
    section: string;
    navigationSelection?: string;
}) => {
    gtmSendEvent({
        event: "track",
        ga4Tracking: {
            ga4_object: "MY_ACCOUNT_NAVIGATION",
            ga4_action: "SELECTED",
            section,
            ...(navigationSelection && { navigationSelection }),
        },
    });
};

const generateNavItems = (
    currentTripsAndDepartures: MyAccountBasicTripAndDeparture[],
    upcomingTripsAndDepartures: MyAccountBasicTripAndDeparture[],
    pastTripsAndDepartures: MyAccountBasicTripAndDeparture[],
    accountL10n: I18nHive<"account">["account"],
    accountNavL10n: I18nHive<"global">["global"]["accountNavigation"],
    customerHasGCDepartures?: boolean,
): NavItemProps[] => {
    const gcToolkitL10n = accountL10n.gcToolkit;
    const bookingNavL10n = accountL10n.navigation.booking;
    const myRewardsEnabled =
        process.env.NEXT_PUBLIC_MY_REWARDS_PAGE_ENABLED === "true";

    const savedQuotesEnabled =
        process.env.NEXT_PUBLIC_UNRELEASED_FEATURE_SAVED_QUOTES === "true";

    const gcToolkitSubItemAnalyticNames = {
        [accountL10n.gcToolkit[0].label.l()]: "Getting started",
        [accountL10n.gcToolkit[1].label.l()]: "Finding the right tour",
        [accountL10n.gcToolkit[2].label.l()]: "Recruiting",
        [accountL10n.gcToolkit[3].label.l()]: "Building loyalty",
        [accountL10n.gcToolkit[4].label.l()]: "Webinars",
    };

    const gcToolkitNavItem =
        customerHasGCDepartures &&
        process.env.NEXT_PUBLIC_IS_GC_TOOLKIT_ENABLED === "true" // Despite it being possible for UB to have GC departures, only GAT has the GC Toolkit
            ? [
                  {
                      icon: LuBook,
                      text: accountNavL10n.gcToolkit.text.l(),
                      url: accountNavL10n.gcToolkit.url.l(),
                      onClick: () =>
                          sendAnalyticsEvent({ section: "GC Toolkit" }),
                      subItems: gcToolkitL10n.map(({ label, value }) => ({
                          text: label.l(),
                          url: value,
                          onClick: () =>
                              sendAnalyticsEvent({
                                  section: "GC Toolkit",
                                  navigationSelection:
                                      gcToolkitSubItemAnalyticNames[label.l()],
                              }),
                      })),
                  },
              ]
            : [];
    const currentAndFutureTrips = [
        ...currentTripsAndDepartures,
        ...upcomingTripsAndDepartures,
    ];
    const pastToursNavItem =
        pastTripsAndDepartures.length > 0
            ? [
                  {
                      icon: LuHistory,
                      text: accountNavL10n.pastTours.text.l(),
                      subItems: pastTripsAndDepartures
                          .map((trip) =>
                              getNavItemsForTrip(trip, bookingNavL10n, true),
                          )
                          .filter((i): i is NavItemProps => !!i),
                      url: accountNavL10n.pastTours.url.l(),
                      onClick: () =>
                          sendAnalyticsEvent({ section: "My Past Tours" }),
                  },
              ]
            : [];

    return [
        {
            icon: FiCompass,
            text: accountNavL10n.myTours.text.l(),
            actionCount: currentAndFutureTrips.reduce(
                (acc, { trip }) =>
                    countActionsPerBooking(trip?.booking?.trips) + acc,
                0,
            ),
            onClick: () =>
                sendAnalyticsEvent({
                    section: "My Tours",
                }),
            subItems: currentAndFutureTrips
                .map((tripAndDeparture) =>
                    getNavItemsForTrip(tripAndDeparture, bookingNavL10n, false),
                )
                .filter((i): i is NavItemProps => !!i),
            url: accountNavL10n.myTours.url.l(),
        },
        ...pastToursNavItem,
        {
            icon: FiUser,
            text: accountNavL10n.profile.text.l(),
            url: accountNavL10n.profile.url.l(),
            onClick: () =>
                sendAnalyticsEvent({
                    section: "Profile",
                }),
            subItems: myRewardsEnabled
                ? [
                      {
                          text: accountNavL10n.myRewards.text.l(),
                          url: accountNavL10n.myRewards.url.l(),
                          onClick: () =>
                              sendAnalyticsEvent({
                                  section: "Profile",
                                  navigationSelection: "My Rewards",
                              }),
                      },
                  ]
                : [],
        },
        {
            icon: FiHeart,
            text: savedQuotesEnabled
                ? accountNavL10n.saved.text.l()
                : accountNavL10n.wishlist.text.l(),
            url: accountNavL10n.saved.url.l(),
            onClick: () =>
                sendAnalyticsEvent({
                    section: "Wishlist",
                }),
        },
        ...gcToolkitNavItem,
    ];
};

const getNavItemsForTrip = (
    tripAndDeparture: MyAccountBasicTripAndDeparture,
    l10n: BookingL10n,
    isPast: boolean,
): NavItemProps | null => {
    const { trip, gcDepartureId, tourName } = tripAndDeparture;
    const isGroupCoordinator =
        trip?.customerType === TripCustomerType.GroupCoordinator ||
        !!gcDepartureId;

    const isEvalReleased = trip?.evaluationRelease?.isReleased ?? false;
    const navItemText = tourName ?? l10n.tourNameUnavailableLabel.l();

    const actionCount =
        !isPast && trip?.booking?.trips
            ? countActionsPerBooking(trip.booking.trips)
            : undefined;

    const bookingId = trip?.booking?._id;
    if (!bookingId) {
        if (!gcDepartureId || !l10n.myGroupMembers) {
            return null;
        } else {
            const url = l10n.myGroupMembers.departureLink.l({
                departureId: gcDepartureId,
                slug: l10n.myGroupMembers.slug.l(),
            });

            return {
                text: navItemText,
                actionCount: actionCount,
                subItems: [
                    {
                        text: l10n.myGroupMembers.text.l(),
                        url: url,
                        onClick: () =>
                            sendAnalyticsEvent({
                                section: "My Tours",
                                navigationSelection: "My Group Members",
                            }),
                    },
                ],
                url,
            };
        }
    }
    const subItems = [];
    if (isPast) {
        if (bookingId) {
            subItems.push({
                text: l10n.tourEval.text.l(),
                url: l10n.bookingLink.l({
                    bookingId: bookingId,
                    slug: l10n.tourEval.slug.l(),
                }),
                onClick: () =>
                    sendAnalyticsEvent({
                        section: "My Past Tours",
                        navigationSelection: "Tour Evaluation",
                    }),
            });

            subItems.push({
                text: l10n.tourReview.text.l(),
                url: l10n.bookingLink.l({
                    bookingId: bookingId,
                    slug: l10n.tourReview.slug.l(),
                }),
                onClick: () =>
                    sendAnalyticsEvent({
                        section: "My Past Tours",
                        navigationSelection: "Tour Review",
                    }),
            });
        }
        if (isGroupCoordinator && l10n.myGroupMembers) {
            const url = l10n.bookingLink.l({
                bookingId: bookingId,
                slug: l10n.myGroupMembers.slug.l(),
            });
            subItems.push({
                text: l10n.myGroupMembers.text.l(),
                url: url,
                onClick: () =>
                    sendAnalyticsEvent({
                        section: "My Past Tours",
                        navigationSelection: "My Group Members",
                    }),
            });
        }
    } else {
        subItems.push(getChecklistNavItem(trip, l10n));
        subItems.push(
            ...getChecklistPeerNavItems(
                bookingId,
                l10n,
                isGroupCoordinator,
                isEvalReleased,
            ),
        );
    }
    return {
        text: navItemText,
        actionCount: actionCount,
        subItems: subItems,
        url: l10n.bookingBaseUrl.l({
            bookingId: bookingId,
        }),
        onClick: () =>
            sendAnalyticsEvent({
                section: `${isPast ? "My Past Tours" : "My Tours"}`,
                navigationSelection: navItemText,
            }),
    };
};

const getChecklistNavItem = (
    tour: MyAccountBasicMyTripFragment,
    l10n: BookingL10n,
): NavItemProps => {
    const checklistLink = l10n.bookingLink.l({
        bookingId: tour.booking?._id ?? "",
        slug: l10n.checklist.slug.l(),
    });
    let tripSpecificChecklistLink = "";

    return {
        text: l10n.checklist.text.l(),
        subItems: tour.booking?.trips.map((trip) => {
            tripSpecificChecklistLink = l10n.tripSpecificBookingLink.l({
                bookingId: tour.booking?._id ?? "",
                tripId: trip._id,
                slug: l10n.checklist.slug.l(),
            });

            return {
                text: trip.customer?.firstName || "",
                actionCount: totalChecklistItemCount(trip),
                url: tripSpecificChecklistLink,
                onClick: () =>
                    sendAnalyticsEvent({
                        section: "My Tours",
                        navigationSelection: "Individual's Checklist",
                    }),
            };
        }),
        url:
            tour.booking?.trips.length === 1
                ? tripSpecificChecklistLink
                : checklistLink,
        onClick: () =>
            sendAnalyticsEvent({
                section: "My Tours",
                navigationSelection: "Checklist",
            }),
    };
};

const getChecklistPeerNavItems = (
    bookingId: string,
    l10n: BookingL10n,
    isGroupCoordinator: boolean,
    isEvalReleased: boolean,
): NavItemProps[] => {
    const names = getChecklistPeerNames(
        l10n,
        isGroupCoordinator,
        isEvalReleased,
    );
    const peerNavItemAnalyticNames = {
        ...(isEvalReleased && { [l10n.tourEval.text.l()]: "Tour Evaluation" }),
        ...(isGroupCoordinator &&
            l10n.myGroupMembers && {
                [l10n.myGroupMembers.text.l()]: "My Group Members",
            }),
        [l10n.travelerDetails.text.l()]: "Traveler Details",
        [l10n.payments.text.l()]: "Payments and Invoice",
        [l10n.itinerary.text.l()]: "Itinerary",
        [l10n.customizationOptions.text.l()]: "Customization Options",
        [l10n.travelProtection.text.l()]: "Travel Protection",
        [l10n.accommodations.text.l()]: "Accommodations",
        [l10n.flights.text.l()]: "Flights",
        [l10n.gettingReady.text.l()]: "Getting Ready",
        [l10n.entryRequirements.text.l()]: "Entry Requirements",
        ...(l10n.bookingPolicies && {
            [l10n.bookingPolicies.text.l()]: "Booking Policies",
        }),
    };
    return names.map(({ text, slug }) => {
        return {
            text: text.l(),
            alwaysExpanded: true,
            url: l10n.bookingLink.l({ bookingId: bookingId, slug: slug.l() }),
            onClick: () =>
                sendAnalyticsEvent({
                    section: "My Tours",
                    navigationSelection: peerNavItemAnalyticNames[text.l()],
                }),
        };
    });
};

export const useNavItems = () => {
    const {
        currentTripsAndDepartures,
        upcomingTripsAndDepartures,
        pastTripsAndDepartures,
    } = useTripsAndDepartures();
    const accountL10n = useI18n("account").account;
    const accountNavL10n = useI18n("global").global.accountNavigation;
    const { customer } = useLightMe();
    return generateNavItems(
        currentTripsAndDepartures.filter(
            ({ trip }) => !trip || trip.status !== TripStatus.Waitlisted,
        ),
        upcomingTripsAndDepartures.filter(
            ({ trip }) => !trip || trip.status !== TripStatus.Waitlisted,
        ),
        pastTripsAndDepartures.filter(
            ({ trip }) => !trip || trip.status !== TripStatus.Waitlisted,
        ),
        accountL10n,
        accountNavL10n,
        !!customer?.hasGCDepartures,
    );
};
